import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/advisors/AdvisorsAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { AdvisorsFilter } from "./advisors-filter/AdvisorsFilter";
import { AdvisorsTable } from "./advisors-table/AdvisorsTable";
import { useAdvisorsUIContext } from "./AdvisorsUIContext";

export function AdvisorsCard() {
  // Advisors Card UI Context
  const advisorsUIContext = useAdvisorsUIContext();
  const advisorsUIProps = useMemo(() => {
    return {
      ids: advisorsUIContext.ids,
      setIds: advisorsUIContext.setIds,
      queryParams: advisorsUIContext.queryParams,
      openNewStudentPage: advisorsUIContext.openNewStudentPage
    };
  }, [advisorsUIContext]);
    
  // Getting curret state of Advisor list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.advisors }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Advisors Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);
  
  /** Belli Bir Danışman Öğretmen Listesini Excele Aktarır */
  const exportAdvisorsToExcel = () => {
    dispatch(actions.getAllAdvisors(advisorsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 30, 30, 15, 30, 15];
      const columns = ["Öğretmen", "Öğrenci Adı", "Öğrenci Soyadı", "Sınıfı", "Şube", "Cinsiyeti"];
      entities.forEach(entity => rows.push([entity.teacher, entity.studentName, entity.studentSurname, entity.grade, entity.section, entity.sexName]));
      const filename = advisorsUIProps.queryParams.term + " - " + (advisorsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Danışman Öğretmen Listesi";
      const sheetname = "Danışman Öğretmen Listesi"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Danışman Öğretmen Listesini PDF e Aktarır */
  const exportAdvisorsToPDF = () => {
    dispatch(actions.getAllAdvisors(advisorsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Öğretmen",
          rows: entities.map(entity => entity.teacher)
        },
        {
          header: "Öğrenci Adı",
          rows: entities.map(entity => entity.studentName)
        },
        {
          header: "Öğrenci Soyadı",
          rows: entities.map(entity => entity.studentSurname)
        },
        {
          header: "Sınıfı",
          rows: entities.map(entity => entity.grade)
        },
        {
          header: "Şube",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Cinsiyeti",
          rows: entities.map(entity => entity.sexName)
        }
      ];

      const filename = advisorsUIProps.queryParams.term + " - " + (advisorsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Danışman Öğretmen Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Danışman Öğretmen Listesini Yazdırma Penceresini Açar */
  const printAdvisors = () => {
    dispatch(actions.getAllAdvisors(advisorsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = advisorsUIProps.queryParams.term + " - " + (advisorsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Danışman Öğretmen Listesi";
      const properties = ["Öğretmen", "Öğrenci Adı", "Öğrenci Soyadı", "Sınıfı", "Şube", "Cinsiyeti"];

      entities.forEach(entity => {
        data.push({
          "Öğretmen": entity.teacher,
          "Öğrenci Adı": entity.studentName,
          "Öğrenci Soyadı": entity.studentSurname,
          "Sınıfı": entity.grade,
          "Şube": entity.section === null ? "" : entity.section,
          "Cinsiyeti": entity.sexName
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Danışman Öğretmen Listesini Panoya Kopyalar */
  const copyAdvisors = () => {
    dispatch(actions.getAllAdvisors(advisorsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Öğretmen", "Öğrenci Adı", "Öğrenci Soyadı", "Sınıfı", "Şube", "Cinsiyeti"];
      const header = advisorsUIProps.queryParams.term + " - " + (advisorsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Danışman Öğretmen Listesi";
      entities.forEach(entity => rows.push([entity.teacher, entity.studentName, entity.studentSurname, entity.grade, entity.section === null ? "" : entity.section, entity.sexName]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Danışman Öğretmen Listesini Siler */
  const deleteAdvisors = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteAdvisors(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        // clear selections list
        advisorsUIProps.setIds([]);
        // refresh list after deletion
        dispatch(actions.getAdvisors(advisorsUIProps.queryParams));        
      }, 2500);
    }
  };

  const title = <>Danışman Öğretmen Listesi{advisorsUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{advisorsUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isEditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportAdvisorsToExcel}
                exportToPDF={exportAdvisorsToPDF}
                print={printAdvisors}
                copy={copyAdvisors}
              />
            }
            {
              isEditionAuthorized &&              
              <FormButton
                path="Navigation/Check"
                title="Öğrenci Seçme"
                method={advisorsUIProps.openNewStudentPage}
              />
            }
            {
              isDeletionAuthorized && 
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deleteAdvisors(advisorsUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <AdvisorsFilter />
        <AdvisorsTable
          deleteAdvisors={deleteAdvisors}
        />
      </CardBody>
    </Card>
  );
}